import * as React from "react";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";

export interface TagInputProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  value?: string[];
  onChange?: (tags: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
  maxTags?: number;
  onTagAdd?: (tag: string) => void;
  onTagRemove?: (tag: string, index: number) => void;
}

const TagInput = React.forwardRef<HTMLDivElement, TagInputProps>(
  (
    {
      className,
      value = [],
      onChange,
      placeholder = "Add tag...",
      disabled = false,
      size = "md",
      maxTags,
      onTagAdd,
      onTagRemove,
      ...props
    },
    ref
  ) => {
    const [inputValue, setInputValue] = React.useState("");
    const inputRef = React.useRef<HTMLInputElement>(null);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" || e.key === ",") {
        e.preventDefault();
        addTag();
      } else if (
        e.key === "Backspace" &&
        inputValue === "" &&
        value.length > 0
      ) {
        removeTag(value.length - 1);
      }
    };

    const addTag = () => {
      const trimmedValue = inputValue.trim();
      if (trimmedValue && !value.includes(trimmedValue)) {
        if (maxTags && value.length >= maxTags) return;

        const newTags = [...value, trimmedValue];
        onChange?.(newTags);
        onTagAdd?.(trimmedValue);
        setInputValue("");
      }
    };

    const removeTag = (index: number) => {
      const tagToRemove = value[index];
      const newTags = value.filter((_, i) => i !== index);
      onChange?.(newTags);
      onTagRemove?.(tagToRemove, index);
    };

    const handleInputBlur = () => {
      addTag();
    };

    return (
      <div
        ref={ref}
        data-slot="tag-input"
        className={cn(
          "wl-input-tag-container",
          size === "lg" && "wl-input-tag-container-lg",
          disabled && "wl-disabled",
          className
        )}
        {...props}
      >
        {value.map((tag, index) => (
          <span key={`${tag}-${index}`} className="wl-tag-badge">
            <span className="wl-tag-text">{tag}</span>
            {!disabled && (
              <button
                type="button"
                className="wl-tag-close"
                onClick={() => removeTag(index)}
                aria-label={`Remove tag ${tag}`}
              >
                <X className="wl-tag-close-icon" />
              </button>
            )}
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          onBlur={handleInputBlur}
          placeholder={value.length === 0 ? placeholder : ""}
          disabled={disabled}
          className={cn(
            "wl-input-text wl-tags-input",
            size === "lg" && "wl-tags-input-lg"
          )}
        />
      </div>
    );
  }
);

TagInput.displayName = "TagInput";

export { TagInput };
